import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useMfaStepUp } from "@/hooks/useMfaStepUp";
import { Seo } from "@/components/seo/Seo";
import { useI18n } from "@/i18n/I18nProvider";

function parseHashParams(hash: string) {
  const raw = (hash || "").replace(/^#/, "");
  const sp = new URLSearchParams(raw);
  return {
    access_token: sp.get("access_token") || "",
    refresh_token: sp.get("refresh_token") || "",
    type: sp.get("type") || "",
    error: sp.get("error") || "",
    error_description: sp.get("error_description") || ""
  };
}

export default function AuthReset() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { ensureAAL2, StepUpDialog } = useMfaStepUp();

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState<string>("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const urlInfo = useMemo(() => {
    const url = new URL(window.location.href);
    return {
      code: url.searchParams.get("code") || "",
      type: url.searchParams.get("type") || "",
      error: url.searchParams.get("error") || "",
      error_description: url.searchParams.get("error_description") || ""
    };
  }, []);

  useEffect(() => {
    const finalizeRecoverySession = async () => {
      try {
        if (urlInfo.error || urlInfo.error_description) {
          throw new Error(urlInfo.error_description || urlInfo.error || t('authreset.invalid_link', 'Invalid reset link'));
        }
        if (urlInfo.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(urlInfo.code);
          if (error) throw error;
          return;
        }
        const hash = parseHashParams(window.location.hash || "");
        if (hash.error || hash.error_description) {
          throw new Error(hash.error_description || hash.error || t('authreset.invalid_link', 'Invalid reset link'));
        }
        if (hash.access_token && hash.refresh_token) {
          const { error } = await supabase.auth.setSession({
            access_token: hash.access_token,
            refresh_token: hash.refresh_token
          });
          if (error) throw error;
          return;
        }
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          throw new Error(t('authreset.invalid_link', 'The reset link is invalid or expired.'));
        }
      } catch (e: any) {
        setLinkError(e?.message || t('authreset.invalid_link', 'The reset link is invalid or expired.'));
      } finally {
        setLoading(false);
      }
    };

    finalizeRecoverySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    const pwd = (password || "").trim();
    if (pwd.length < 6) {
      toast({ title: t('authreset.error_title', 'Error'), description: t('authreset.error_short', 'Password must be at least 6 characters.'), variant: "destructive" });
      return;
    }
    if (pwd !== (confirmPassword || "").trim()) {
      toast({ title: t('authreset.error_title', 'Error'), description: t('authreset.error_mismatch', 'Passwords do not match.'), variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const ok = await ensureAAL2();
      if (!ok) {
        toast({ title: t('authreset.verification_required_title', 'Verification required'), description: t('authreset.verification_required_desc', 'Please verify with your authenticator code to continue.'), variant: "destructive" });
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;

      toast({ title: t('authreset.success_title', 'Password updated'), description: t('authreset.success_desc', 'You can now sign in with your new password.') });

      try { await supabase.auth.signOut(); } catch {}
      navigate("/auth");
    } catch (e: any) {
      const msg = String(e?.message || "").toLowerCase();
      if (msg.includes("rate limit")) {
        toast({
          title: t('authreset.too_many_attempts_title', 'Too many attempts'),
          description: t('authreset.too_many_attempts_desc', 'You have made too many attempts in a short time. Please retry in a few minutes.'),
          variant: "destructive",
        });
      } else {
        toast({ title: t('authreset.error_title', 'Error'), description: e?.message || t('authreset.update_failed', 'Failed to update password.'), variant: "destructive" });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <Seo title={t('authreset.seo_title', 'Reset password | Halomora')} noindex canonicalPath="/auth/reset" />
      {StepUpDialog}
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('authreset.title', 'Reset your password')}</CardTitle>
          <CardDescription>
            {t('authreset.subtitle', 'Choose a new password for your account.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">{t('authreset.validating', 'Validating link…')}</div>
          ) : linkError ? (
            <div className="space-y-3">
              <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2">
                {linkError}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate("/auth")}>{t('authreset.go_signin', 'Go to sign in')}</Button>
                <Button onClick={() => navigate("/profile")}>{t('authreset.try_from_profile', 'Try again from Profile')}</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('authreset.new_password', 'New password')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('authreset.confirm_password', 'Confirm new password')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <Button className="w-full" onClick={onSubmit} disabled={saving}>
                {saving ? t('authreset.saving', 'Saving…') : t('authreset.cta', 'Update password')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
